import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  MdArrowBack,
  MdCheck,
  MdDownload,
  MdUploadFile,
  MdPersonOutline,
  MdSchool,
  MdFamilyRestroom,
  MdSupervisorAccount,
  MdContactPhone,
  MdHistoryEdu,
} from "react-icons/md";
import {
  API_BASE_URL,
  getAuthHeaders,
  getJsonAuthHeaders,
} from "../../../utils/api";
import {
  classBelongsToSession,
  classSectionsOf,
  filterClassesBySession,
} from "../../../utils/campusClasses";

const normalizedClassName = (value) =>
  String(value || "").trim().toLowerCase();

const requiresPreviousAcademicDetails = (selectedClass, availableClasses) => {
  if (!selectedClass || !Array.isArray(availableClasses) || !availableClasses.length) {
    return false;
  }

  const distinctClasses = availableClasses.filter(
    (classItem, index, list) =>
      list.findIndex(
        (candidate) =>
          normalizedClassName(candidate.name) ===
          normalizedClassName(classItem.name),
      ) === index,
  );
  const lowestClass = distinctClasses[0];
  return Boolean(
    lowestClass &&
      normalizedClassName(selectedClass.name) !==
        normalizedClassName(lowestClass.name),
  );
};

function StudentForm({
  onSave,
  onCancel,
  userRole,
  initialData,
  showToast,
  onLogout,
}) {
  // Records created before father/guardian were split kept the father's phone,
  // CNIC and address in the guardian_* columns.
  const legacyFatherContact = !initialData?.guardian_relation;

  const [formData, setFormData] = useState({
    name: initialData?.name || "",
    father_name: initialData?.father_name || "",
    father_phone:
      initialData?.father_phone ||
      (legacyFatherContact ? initialData?.guardian_phone || "" : ""),
    father_cnic:
      initialData?.father_cnic ||
      (legacyFatherContact ? initialData?.guardian_cnic || "" : ""),
    father_address:
      initialData?.father_address ||
      (legacyFatherContact ? initialData?.guardian_address || "" : ""),
    cnic_bform: initialData?.cnic_bform || "",
    current_address: initialData?.current_address || "",
    permanent_address: initialData?.permanent_address || "",
    is_transfer_student: Boolean(initialData?.is_transfer_student),
    previous_school_name: initialData?.previous_school_name || "",
    previous_class: initialData?.previous_class || "",
    previous_grade: initialData?.previous_grade || "",
    previous_academic_record: initialData?.previous_academic_record || "",
    gender: initialData?.gender || "",
    date_of_birth: initialData?.date_of_birth || "",
    campus_id: initialData?.campus_id?.toString() || "",
    class_id: initialData?.class_id?.toString() || "",
    class_name: initialData?.class_name || "",
    class_section: initialData?.class_section || "",
    admission_date: initialData?.admission_date || "",
    student_status: initialData?.student_status || "Active",
    fee_amount: initialData?.actual_fee_amount?.toString() || "",
    fee_plan_id: initialData?.fee_plan_id?.toString() || "",
    fee_plan_name: initialData?.fee_plan_name || "",
    scholarship_id: initialData?.scholarship_id?.toString() || "",
    scholarship_option: "",
    custom_scholarship_amount: "",
    fee_status: initialData?.fee_status || "Unpaid",
    student_id: initialData?.student_id || "",
    academic_session: initialData?.academic_session || "",
    guardian_name: initialData?.guardian_name || "",
    guardian_relation: initialData?.guardian_relation || "",
    guardian_phone: legacyFatherContact ? "" : initialData?.guardian_phone || "",
    guardian_email: initialData?.guardian_email || "",
    guardian_address: legacyFatherContact
      ? ""
      : initialData?.guardian_address || "",
    guardian_cnic: legacyFatherContact ? "" : initialData?.guardian_cnic || "",
    emergency_contact_name: initialData?.emergency_contact_name || "",
    emergency_contact_number: initialData?.emergency_contact_number || "",
    photo_data: initialData?.photo_data || "",
  });
  const [campuses, setCampuses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [errors, setErrors] = useState({});
  const [bulkUploadState, setBulkUploadState] = useState({
    status: "idle",
    message: "",
  });
  const [bulkUploadErrors, setBulkUploadErrors] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(
    initialData?.photo_data || "",
  );
  const [showRegistrationSuccess, setShowRegistrationSuccess] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState({
    title: "Student Registered Successfully",
    message: "",
  });
  const [parentCredentials, setParentCredentials] = useState(null);
  const bulkFileInputRef = useRef(null);

  const isEdit = Boolean(initialData);
  const [mode, setMode] = useState(isEdit ? "single" : "choose");

  const enterMode = (nextMode) => {
    window.history.pushState({ studentFormMode: nextMode }, "");
    setMode(nextMode);
  };

  useEffect(() => {
    if (isEdit) return undefined;
    const handlePopState = () => setMode("choose");
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isEdit]);

  useEffect(() => {
    if (userRole !== "superadmin") return;

    const loadCampuses = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/campuses`, {
          headers: getAuthHeaders(),
        });
        if (res.status === 401) {
          onLogout?.();
          return;
        }
        const data = res.ok ? await res.json() : [];
        setCampuses(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load campuses", err);
      }
    };

    loadCampuses();
  }, [userRole, onLogout]);

  const getErrorMessage = useCallback(async (res, fallback) => {
    const data = await res.json().catch(() => ({}));
    if (Array.isArray(data.detail)) {
      return data.detail.map((err) => err.msg).join(", ") || fallback;
    }
    return data.detail || fallback;
  }, []);

  useEffect(() => {
    const campusId = formData.campus_id;
    if (userRole === "superadmin" && !campusId) {
      setSessions([]);
      setClasses([]);
      return;
    }

    const loadCampusOptions = async () => {
      setIsLoadingOptions(true);
      try {
        const campusQuery = campusId ? `?campus_id=${campusId}` : "";
        const [sessionsRes, classesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/sessions${campusQuery}`, {
            headers: getAuthHeaders(),
          }),
          fetch(`${API_BASE_URL}/api/classes${campusQuery}`, {
            headers: getAuthHeaders(),
          }),
        ]);

        if (sessionsRes.status === 401 || classesRes.status === 401) {
          onLogout?.();
          return;
        }
        if (!sessionsRes.ok) {
          throw new Error(
            await getErrorMessage(sessionsRes, "Could not load sessions."),
          );
        }
        if (!classesRes.ok) {
          throw new Error(
            await getErrorMessage(classesRes, "Could not load classes."),
          );
        }

        const [sessionsData, classesData] = await Promise.all([
          sessionsRes.json(),
          classesRes.json(),
        ]);
        setSessions(Array.isArray(sessionsData) ? sessionsData : []);
        setClasses(Array.isArray(classesData) ? classesData : []);
      } catch (err) {
        console.error("Failed to load campus options", err);
        showToast?.(
          "error",
          "Load Error",
          err.message || "Could not load campus options.",
        );
        setSessions([]);
        setClasses([]);
      } finally {
        setIsLoadingOptions(false);
      }
    };

    loadCampusOptions();
  }, [formData.campus_id, getErrorMessage, onLogout, showToast, userRole]);

  const sessionClasses = classes.filter((classItem) => {
    if (!formData.academic_session) return true;
    const selectedSession = sessions.find(
      (session) => session.name === formData.academic_session,
    );
    if (!selectedSession) return false;
    return classBelongsToSession(classItem, selectedSession.id);
  });
  const selectedEnrollmentClass = sessionClasses.find(
    (classItem) => String(classItem.id) === String(formData.class_id),
  );
  const enrollmentSectionOptions = selectedEnrollmentClass
    ? classSectionsOf(selectedEnrollmentClass)
    : [];
  const needsPreviousAcademicDetails = requiresPreviousAcademicDetails(
    selectedEnrollmentClass,
    sessionClasses,
  );

  const validate = () => {
    const errs = {};

    if (!formData.name.trim()) {
      errs.name = "Student name is required";
    }
    if (!formData.gender) {
      errs.gender = "Gender is required";
    }
    if (!formData.date_of_birth) {
      errs.date_of_birth = "Date of birth is required";
    }
    if (!formData.admission_date) {
      errs.admission_date = "Admission date is required";
    }
    if (userRole === "superadmin" && !formData.campus_id) {
      errs.campus_id = "Campus is required for superadmin";
    }
    if (!formData.academic_session) {
      errs.academic_session = "Academic session is required";
    }
    if (!formData.class_id) {
      errs.class_id = "Enrollment class is required";
    }
    if (!formData.class_section.trim()) {
      errs.class_section = "Section is required";
    }
    if (needsPreviousAcademicDetails) {
      if (!formData.previous_school_name.trim()) {
        errs.previous_school_name = "Previous school name is required";
      }
      if (!formData.previous_class.trim()) {
        errs.previous_class = "Previous class is required";
      }
      if (!formData.previous_grade.trim()) {
        errs.previous_grade = "Last class grade or percentage is required";
      }
      if (!formData.previous_academic_record.trim()) {
        errs.previous_academic_record = "Previous academic remarks are required";
      }
    }

    if (!formData.father_name.trim()) {
      errs.father_name = "Father name is required";
    }
    if (!formData.father_phone.trim()) {
      errs.father_phone = "Father contact number is required";
    }
    if (!formData.father_cnic.trim()) {
      errs.father_cnic = "Father CNIC is required";
    }
    if (!formData.father_address.trim()) {
      errs.father_address = "Father address is required";
    }

    const hasGuardianDetails = Boolean(
      formData.guardian_name.trim() ||
        formData.guardian_relation.trim() ||
        formData.guardian_phone.trim() ||
        formData.guardian_cnic.trim() ||
        formData.guardian_address.trim(),
    );
    if (hasGuardianDetails) {
      if (!formData.guardian_name.trim()) {
        errs.guardian_name = "Guardian name is required";
      }
      if (!formData.guardian_relation.trim()) {
        errs.guardian_relation = "Relation to student is required";
      }
      if (!formData.guardian_phone.trim()) {
        errs.guardian_phone = "Guardian contact number is required";
      }
      if (!formData.guardian_cnic.trim()) {
        errs.guardian_cnic = "Guardian CNIC is required";
      }
    }

    if (
      formData.guardian_email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.guardian_email)
    ) {
      errs.guardian_email = "Enter a valid email";
    }
    if (!formData.emergency_contact_name.trim()) {
      errs.emergency_contact_name = "Emergency contact name is required";
    }

    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      if (name === "campus_id") {
        return {
          ...prev,
          campus_id: value,
          academic_session: "",
          class_id: "",
          class_name: "",
          class_section: "",
        };
      }
      if (name === "is_transfer_student") {
        const checked = value === "no";
        return {
          ...prev,
          is_transfer_student: checked,
          previous_school_name: checked ? prev.previous_school_name : "",
          previous_class: checked ? prev.previous_class : "",
          previous_grade: checked ? prev.previous_grade : "",
          previous_academic_record: checked ? prev.previous_academic_record : "",
        };
      }
      if (name === "academic_session") {
        return {
          ...prev,
          academic_session: value,
          class_id: "",
          class_name: "",
          class_section: "",
        };
      }
      if (name === "class_id") {
        const selectedClass = classes.find(
          (classItem) => String(classItem.id) === String(value),
        );
        const requiresPrevious = requiresPreviousAcademicDetails(
          selectedClass,
          sessionClasses,
        );
        return {
          ...prev,
          class_id: value,
          class_name: selectedClass?.name || "",
          class_section: classSectionsOf(selectedClass)[0] || "",
          is_transfer_student: requiresPrevious,
          previous_school_name: requiresPrevious
            ? prev.previous_school_name
            : "",
          previous_class: requiresPrevious ? prev.previous_class : "",
          previous_grade: requiresPrevious ? prev.previous_grade : "",
          previous_academic_record: requiresPrevious
            ? prev.previous_academic_record
            : "",
        };
      }
      return { ...prev, [name]: value };
    });
    if (name === "is_transfer_student" && value === "yes") {
      setErrors((prev) => ({
        ...prev,
        previous_school_name: "",
        previous_class: "",
        previous_grade: "",
        previous_academic_record: "",
      }));
    }
    if (name === "class_id") {
      setErrors((prev) => ({
        ...prev,
        class_id: "",
        class_section: "",
      }));
    }
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result;
      setFormData((prev) => ({ ...prev, photo_data: base64 }));
      setPhotoPreview(base64);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setIsSaving(true);
    const payload = {
      ...formData,
      is_transfer_student: needsPreviousAcademicDetails,
      father_name: formData.father_name.trim(),
      father_phone: formData.father_phone.trim(),
      father_cnic: formData.father_cnic.trim(),
      father_address: formData.father_address.trim(),
      guardian_name: formData.guardian_name.trim(),
      guardian_relation: formData.guardian_relation.trim(),
      guardian_phone: formData.guardian_phone.trim(),
      guardian_email: formData.guardian_email.trim(),
      guardian_cnic: formData.guardian_cnic.trim(),
      guardian_address: formData.guardian_address.trim(),
      emergency_contact_name: formData.emergency_contact_name.trim(),
      emergency_contact_number: formData.emergency_contact_number.trim(),
      previous_school_name: needsPreviousAcademicDetails
        ? formData.previous_school_name.trim()
        : "",
      previous_class: needsPreviousAcademicDetails
        ? formData.previous_class.trim()
        : "",
      previous_grade: needsPreviousAcademicDetails
        ? formData.previous_grade.trim()
        : "",
      previous_academic_record: needsPreviousAcademicDetails
        ? formData.previous_academic_record.trim()
        : "",
    };
    const saved = await onSave(payload);
    setIsSaving(false);

    if (saved && !initialData) {
      const creds = saved?.parent_credentials || null;
      if (creds?.username) {
        setParentCredentials(creds);
        setRegistrationSuccess({
          title: "Student Registered Successfully",
          message: `${formData.name} has been added. Share the parent login credentials below.`,
        });
        setShowRegistrationSuccess(true);
      } else {
        setParentCredentials(null);
        setRegistrationSuccess({
          title: "Student Registered Successfully",
          message: `${formData.name} has been added to the school records.`,
        });
        setShowRegistrationSuccess(true);
        setTimeout(() => {
          setShowRegistrationSuccess(false);
          onCancel();
        }, 1600);
      }
    }
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBulkUploadErrors([]);
    setBulkUploadState({
      status: "uploading",
      message: "Uploading students...",
    });

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const csv = event.target.result;
        const firstLine = csv.split(/\r?\n/)[0];
        const tabCount = (firstLine.match(/\t/g) || []).length;
        const commaCount = (firstLine.match(/,/g) || []).length;
        const delimiter = tabCount > commaCount ? "\t" : ",";

        const parseCSVLine = (line, delim) => {
          if (delim === "\t") return line.split("\t").map((v) => v.trim());
          const out = [];
          let current = "";
          let inQuotes = false;

          for (let i = 0; i < line.length; i += 1) {
            const ch = line[i];
            if (ch === '"') {
              if (inQuotes && line[i + 1] === '"') {
                current += '"';
                i += 1;
              } else {
                inQuotes = !inQuotes;
              }
            } else if (ch === "," && !inQuotes) {
              out.push(current);
              current = "";
            } else {
              current += ch;
            }
          }

          out.push(current);
          return out.map((v) => v.trim());
        };

        const lines = csv
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter(Boolean);
        const headers = parseCSVLine(lines[0], delimiter);
        const students = [];

        for (let i = 1; i < lines.length; i += 1) {
          const values = parseCSVLine(lines[i], delimiter);
          if (!values[0]) continue;

          const row = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || null;
          });

          const safeStr = (value) =>
            value && typeof value === "string" ? value.trim() : null;
          const safeInt = (value) => {
            if (!value) return null;
            const parsed = parseInt(value, 10);
            return Number.isNaN(parsed) ? null : parsed;
          };
          const safeBool = (value) =>
            ["yes", "true", "1", "transfer", "transferring"].includes(
              String(value || "").trim().toLowerCase(),
            );

          const monthlyFeeText =
            safeStr(row["Monthly Fee"]) || safeStr(row.monthly_fee);

          students.push({
            name:
              safeStr(row["Student Name"]) ||
              safeStr(row.Student_Name) ||
              safeStr(row.name),
            student_name:
              safeStr(row["Student Name"]) ||
              safeStr(row.Student_Name) ||
              safeStr(row.name),
            father_name:
              safeStr(row["Father Name"]) ||
              safeStr(row.Father_Name) ||
              safeStr(row.father_name),
            father_phone:
              safeStr(row["Father Contact Number"]) ||
              safeStr(row.father_phone),
            father_cnic:
              safeStr(row["Father CNIC"]) || safeStr(row.father_cnic),
            father_address:
              safeStr(row["Father Address"]) || safeStr(row.father_address),
            cnic_bform:
              safeStr(row["Student CNIC/B-Form"]) || safeStr(row.cnic_bform),
            current_address:
              safeStr(row["Current Address"]) || safeStr(row.current_address),
            permanent_address:
              safeStr(row["Permanent Address"]) ||
              safeStr(row.permanent_address),
            guardian_name:
              safeStr(row["Guardian Name"]) || safeStr(row.guardian_name),
            guardian_relation:
              safeStr(row["Guardian Relation"]) ||
              safeStr(row.guardian_relation),
            guardian_phone:
              safeStr(row["Guardian Contact Number"]) ||
              safeStr(row.guardian_phone),
            guardian_email:
              safeStr(row["Parent Email"]) || safeStr(row.guardian_email),
            guardian_cnic:
              safeStr(row["Guardian CNIC"]) || safeStr(row.guardian_cnic),
            guardian_address:
              safeStr(row["Guardian Address"]) || safeStr(row.guardian_address),
            emergency_contact_name:
              safeStr(row["Emergency Contact Name"]) ||
              safeStr(row.emergency_contact_name),
            emergency_contact_number:
              safeStr(row["Emergency Contact Number"]) ||
              safeStr(row.emergency_contact_number),
            is_transfer_student: safeBool(
              row["Is Transfer Student (Yes/No)"] ??
                row.is_transfer_student,
            ),
            previous_school_name:
              safeStr(row["Previous School Name"]) ||
              safeStr(row.previous_school_name),
            previous_class:
              safeStr(row["Previous Class"]) || safeStr(row.previous_class),
            previous_grade:
              safeStr(row["Last Class Grade/Percentage"]) ||
              safeStr(row["Previous Grade/Result"]) ||
              safeStr(row.previous_grade),
            previous_academic_record:
              safeStr(row["Previous Academic Remarks"]) ||
              safeStr(row["Previous Academic Record"]) ||
              safeStr(row.previous_academic_record),
            gender: safeStr(row.Gender) || safeStr(row.gender),
            date_of_birth:
              safeStr(row["Date of Birth"]) ||
              safeStr(row.Date_of_Birth) ||
              safeStr(row.date_of_birth),
            dob:
              safeStr(row["Date of Birth"]) ||
              safeStr(row.Date_of_Birth) ||
              safeStr(row.date_of_birth),
            admission_date:
              safeStr(row["Admission Date"]) || safeStr(row.admission_date),
            class_id: null,
            class_name:
              safeStr(row["Class Name"]) || safeStr(row.class_name),
            class_field:
              safeStr(row["Class Name"]) || safeStr(row.class_field),
            section: safeStr(row.Section) || safeStr(row.section),
            class_section:
              safeStr(row.Section) || safeStr(row.class_section),
            monthly_fee: monthlyFeeText,
            fee_amount: safeStr(row["Monthly Fee"]) || safeStr(row.fee_amount),
            fee_plan_name:
              safeStr(row["Fee Plan"]) || safeStr(row.fee_plan_name),
            fee_plan: safeStr(row["Fee Plan"]) || safeStr(row.fee_plan),
            student_status:
              safeStr(row["Student Status"]) ||
              safeStr(row.Status) ||
              safeStr(row.student_status),
            status:
              safeStr(row["Student Status"]) ||
              safeStr(row.Status) ||
              safeStr(row.status),
            fee_status:
              safeStr(row["Fee Status(Paid/Unpaid)"]) ||
              safeStr(row.fee_status),
            fee_status_paid_unpaid:
              safeStr(row["Fee Status(Paid/Unpaid)"]) ||
              safeStr(row.fee_status),
            scholarship_id:
              safeInt(row.scholarship_id) || safeInt(row["Scholarship ID"]),
            scholarship:
              safeStr(row.Scholarship) ||
              safeStr(row.scholarship) ||
              (/schol[ao]r/i.test(monthlyFeeText || "")
                ? monthlyFeeText
                : null),
            scholarship_type:
              safeStr(row["Scholarship Type"]) || safeStr(row.scholarship_type),
            withdrawal_date:
              safeStr(row["Withdrawal Date"]) || safeStr(row.withdrawal_date),
            withdrawal_reason:
              safeStr(row["Withdrawal Reason"]) ||
              safeStr(row.withdrawal_reason),
            academic_session:
              safeStr(row["Academic Session"]) ||
              safeStr(row.academic_session),
          });
        }

        if (students.length === 0) {
          setBulkUploadState({
            status: "error",
            message: "No valid records found in the selected file.",
          });
          showToast?.("error", "CSV Error", "No valid student records found");
          return;
        }

        // Admins are scoped to their own campus by the backend; only
        // superadmin picks a campus explicitly in this form.
        let campusId = null;
        if (userRole === "superadmin") {
          campusId = parseInt(formData.campus_id, 10);
          if (Number.isNaN(campusId) || campusId <= 0) {
            setBulkUploadState({
              status: "error",
              message: "Campus is required for bulk upload.",
            });
            showToast?.(
              "error",
              "Upload Error",
              "Please select a campus before uploading.",
            );
            return;
          }
        }

        // The values selected above are defaults. Template rows may provide
        // their own session/class/section when a mixed-class CSV is uploaded.
        const selectedClassId = formData.class_id
          ? parseInt(formData.class_id, 10)
          : null;
        students.forEach((s) => {
          const hasCsvClass = Boolean(s.class_name || s.class_field);
          s.class_id = hasCsvClass ? null : selectedClassId;
          s.class_name = s.class_name || formData.class_name || null;
          s.class_field = s.class_field || s.class_name || null;
          s.section = s.section || formData.class_section || null;
          s.class_section = s.class_section || s.section || null;
          s.academic_session =
            s.academic_session || formData.academic_session || null;
          const rowSession = sessions.find(
            (session) => session.name === s.academic_session,
          );
          const rowSessionClasses = rowSession
            ? filterClassesBySession(classes, rowSession.id)
            : classes;
          const rowClass = rowSessionClasses.find(
            (classItem) =>
              (s.class_id && String(classItem.id) === String(s.class_id)) ||
              normalizedClassName(classItem.name) ===
                normalizedClassName(s.class_name),
          );
          s.is_transfer_student = requiresPreviousAcademicDetails(
            rowClass,
            rowSessionClasses,
          );
        });

        const res = await fetch(`${API_BASE_URL}/api/students/bulk`, {
          method: "POST",
          headers: getJsonAuthHeaders(),
          body: JSON.stringify({ campus_id: campusId, students }),
        });

        if (res.status === 401) {
          onLogout?.();
          return;
        }
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          if (
            res.status === 422 &&
            errData.detail &&
            Array.isArray(errData.detail)
          ) {
            throw new Error(
              `Validation Error:\n${errData.detail
                .map(
                  (err) =>
                    `Field: ${
                      Array.isArray(err.loc) ? err.loc.join(".") : "unknown"
                    } - ${err.msg}`,
                )
                .join("\n")}`,
            );
          }
          throw new Error(errData.detail || "Unknown error");
        }

        const result = await res.json();
        const resultErrors = Array.isArray(result.errors) ? result.errors : [];
        if (result.error_count > 0) {
          setBulkUploadErrors(resultErrors);
          setBulkUploadState({
            status: "warning",
            message: `Upload completed with ${result.error_count} issue${
              result.error_count > 1 ? "s" : ""
            }. Download error report.`,
          });
          showToast?.(
            "warning",
            "Bulk Upload Partial",
            `${result.success_count} students uploaded. ${result.error_count} records need review.`,
          );
        } else {
          setBulkUploadErrors([]);
          setBulkUploadState({
            status: "success",
            message: `Upload complete. ${result.success_count} students added.`,
          });
          showToast?.(
            "success",
            "Bulk Upload Complete",
            `${result.success_count} students registered successfully`,
          );
          setRegistrationSuccess({
            title: "Bulk Registration Successful",
            message: `${result.success_count} students have been added from the CSV file.`,
          });
          setShowRegistrationSuccess(true);
          setTimeout(() => {
            setShowRegistrationSuccess(false);
            onCancel();
          }, 1800);
        }
        e.target.value = "";
      } catch (err) {
        console.error(err);
        setBulkUploadErrors([]);
        setBulkUploadState({
          status: "error",
          message: "Upload failed. Please try again.",
        });
        showToast?.("error", "Upload Failed", err.message);
        e.target.value = "";
      }
    };
    reader.readAsText(file);
  };

  const downloadBulkTemplate = () => {
    const headers = [
      "Student Name",
      "Gender",
      "Date of Birth",
      "Admission Date",
      "Academic Session",
      "Class Name",
      "Section",
      "Father Name",
      "Father Contact Number",
      "Father CNIC",
      "Father Address",
      "Emergency Contact Name",
      "Student CNIC/B-Form",
      "Current Address",
      "Permanent Address",
      "Parent Email",
      "Guardian Name",
      "Guardian Relation",
      "Guardian Contact Number",
      "Guardian CNIC",
      "Guardian Address",
      "Emergency Contact Number",
      "Previous School Name",
      "Previous Class",
      "Last Class Grade/Percentage",
      "Previous Academic Remarks",
      "Monthly Fee",
    ];
    const csv = headers.join(",") + "\n";
    const url = window.URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8;" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = "student_bulk_upload_template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const downloadBulkErrorReport = () => {
    if (!bulkUploadErrors.length) return;
    const escapeCsv = (value) =>
      `"${String(value == null ? "" : value).replace(/"/g, '""')}"`;
    const csv = [
      ["row", "name", "error"],
      ...bulkUploadErrors.map((error) => [
        error?.row ?? "",
        error?.name ?? "",
        error?.error ?? "",
      ]),
    ]
      .map((columns) => columns.map(escapeCsv).join(","))
      .join("\n");
    const url = window.URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8;" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `bulk_error_${new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/[:T]/g, "-")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const renderFieldError = (field) =>
    errors[field] ? (
      <span className="enrollment-error" role="alert">
        {errors[field]}
      </span>
    ) : null;

  const renderSectionHeading = (icon, title, hint) => (
    <div className="enrollment-divider">
      <span className="enrollment-divider-icon">{icon}</span>
      <span className="enrollment-divider-text">
        {title}
        {hint ? <small>{hint}</small> : null}
      </span>
    </div>
  );

  const renderPersonalFields = () => (
    <>
      {renderSectionHeading(
        <MdPersonOutline />,
        "Student Information",
        "Personal identity and residence details of the student.",
      )}

      <div className="student-photo-row">
        <div className="student-photo-column">
          <label className="student-photo-title" htmlFor="student-photo">
            Student Photo
          </label>
          <label htmlFor="student-photo" className="student-photo-picker">
            {photoPreview ? (
              <img src={photoPreview} alt="Student" />
            ) : (
              <span>Upload a round student photo</span>
            )}
          </label>
          <input
            id="student-photo"
            name="photo_data"
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            className="student-photo-input"
          />
          {photoPreview && (
            <button
              type="button"
              className="student-photo-remove"
              onClick={() => {
                setFormData((prev) => ({ ...prev, photo_data: "" }));
                setPhotoPreview("");
              }}
            >
              Remove photo
            </button>
          )}
        </div>

        <div className="student-photo-info">
          <div className="enrollment-field">
            <label htmlFor="student-name">Student Name *</label>
            <input
              id="student-name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter student's full name"
              className={errors.name ? "has-error" : ""}
            />
            {renderFieldError("name")}
          </div>
        </div>
      </div>

      <div className="enrollment-field">
        <label htmlFor="student-gender">Gender *</label>
        <select
          id="student-gender"
          name="gender"
          value={formData.gender}
          onChange={handleChange}
          className={errors.gender ? "has-error" : ""}
        >
          <option value="">Select gender</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
          <option value="Other">Other</option>
        </select>
        {renderFieldError("gender")}
      </div>

      <div className="enrollment-field">
        <label htmlFor="student-cnic-bform">CNIC / B-Form</label>
        <input
          id="student-cnic-bform"
          name="cnic_bform"
          type="text"
          value={formData.cnic_bform}
          onChange={handleChange}
          placeholder="Enter student CNIC or B-Form number"
        />
      </div>

      <div className="enrollment-field">
        <label htmlFor="student-dob">Date of Birth *</label>
        <input
          id="student-dob"
          name="date_of_birth"
          type="date"
          required
          value={formData.date_of_birth}
          onChange={handleChange}
          className={errors.date_of_birth ? "has-error" : ""}
        />
        {renderFieldError("date_of_birth")}
      </div>

      <div className="enrollment-field">
        <label htmlFor="student-admission-date">Admission Date *</label>
        <input
          id="student-admission-date"
          name="admission_date"
          type="date"
          value={formData.admission_date}
          onChange={handleChange}
          className={errors.admission_date ? "has-error" : ""}
        />
        {renderFieldError("admission_date")}
      </div>

      <div className="enrollment-field span-2">
        <label htmlFor="student-current-address">Current Address</label>
        <textarea
          id="student-current-address"
          name="current_address"
          value={formData.current_address}
          onChange={handleChange}
          placeholder="Enter student's current address"
          rows={3}
        />
      </div>

      <div className="enrollment-field span-2">
        <label htmlFor="student-permanent-address">Permanent Address</label>
        <textarea
          id="student-permanent-address"
          name="permanent_address"
          value={formData.permanent_address}
          onChange={handleChange}
          placeholder="Enter student's permanent address"
          rows={3}
        />
      </div>

      {renderSectionHeading(
        <MdFamilyRestroom />,
        "Father's Information",
        "Recorded separately from the guardian.",
      )}

      <div className="enrollment-field">
        <label htmlFor="student-father-name">Father Name *</label>
        <input
          id="student-father-name"
          name="father_name"
          type="text"
          value={formData.father_name}
          onChange={handleChange}
          placeholder="Enter father's full name"
          className={errors.father_name ? "has-error" : ""}
        />
        {renderFieldError("father_name")}
      </div>

      <div className="enrollment-field">
        <label htmlFor="father-phone">Father Contact Number *</label>
        <input
          id="father-phone"
          name="father_phone"
          type="tel"
          value={formData.father_phone}
          onChange={handleChange}
          placeholder="e.g. 03XX-XXXXXXX"
          className={errors.father_phone ? "has-error" : ""}
        />
        {renderFieldError("father_phone")}
      </div>

      <div className="enrollment-field">
        <label htmlFor="father-cnic">Father CNIC *</label>
        <input
          id="father-cnic"
          name="father_cnic"
          type="text"
          value={formData.father_cnic}
          onChange={handleChange}
          placeholder="12345-1234567-1"
          className={errors.father_cnic ? "has-error" : ""}
        />
        {renderFieldError("father_cnic")}
      </div>

      <div className="enrollment-field">
        <label htmlFor="guardian-email">Parent Email</label>
        <input
          id="guardian-email"
          name="guardian_email"
          type="email"
          value={formData.guardian_email}
          onChange={handleChange}
          placeholder="Enter email (optional)"
          className={errors.guardian_email ? "has-error" : ""}
        />
        {renderFieldError("guardian_email")}
      </div>

      <div className="enrollment-field span-2">
        <label htmlFor="father-address">Father Address *</label>
        <textarea
          id="father-address"
          name="father_address"
          value={formData.father_address}
          onChange={handleChange}
          placeholder="Enter father's address"
          rows={2}
          className={errors.father_address ? "has-error" : ""}
        />
        {renderFieldError("father_address")}
      </div>

      {renderSectionHeading(
        <MdSupervisorAccount />,
        "Guardian's Information",
        "Optional — fill in only if someone other than the father is the caretaker.",
      )}

      <div className="enrollment-field">
        <label htmlFor="guardian-name">Guardian Name</label>
        <input
          id="guardian-name"
          name="guardian_name"
          type="text"
          value={formData.guardian_name}
          onChange={handleChange}
          placeholder="Enter guardian's full name"
          className={errors.guardian_name ? "has-error" : ""}
        />
        {renderFieldError("guardian_name")}
      </div>

      <div className="enrollment-field">
        <label htmlFor="guardian-relation">Relation to Student</label>
        <select
          id="guardian-relation"
          name="guardian_relation"
          value={formData.guardian_relation}
          onChange={handleChange}
          className={errors.guardian_relation ? "has-error" : ""}
        >
          <option value="">Select relation</option>
          <option value="Mother">Mother</option>
          <option value="Uncle">Uncle</option>
          <option value="Aunt">Aunt</option>
          <option value="Brother">Brother</option>
          <option value="Sister">Sister</option>
          <option value="Grandfather">Grandfather</option>
          <option value="Grandmother">Grandmother</option>
          <option value="Other">Other</option>
        </select>
        {renderFieldError("guardian_relation")}
      </div>

      <div className="enrollment-field">
        <label htmlFor="guardian-phone">Guardian Contact Number</label>
        <input
          id="guardian-phone"
          name="guardian_phone"
          type="tel"
          value={formData.guardian_phone}
          onChange={handleChange}
          placeholder="e.g. 03XX-XXXXXXX"
          className={errors.guardian_phone ? "has-error" : ""}
        />
        {renderFieldError("guardian_phone")}
      </div>

      <div className="enrollment-field">
        <label htmlFor="guardian-cnic">Guardian CNIC</label>
        <input
          id="guardian-cnic"
          name="guardian_cnic"
          type="text"
          value={formData.guardian_cnic}
          onChange={handleChange}
          placeholder="12345-1234567-1"
          className={errors.guardian_cnic ? "has-error" : ""}
        />
        {renderFieldError("guardian_cnic")}
      </div>

      <div className="enrollment-field span-2">
        <label htmlFor="guardian-address">Guardian Address</label>
        <textarea
          id="guardian-address"
          name="guardian_address"
          value={formData.guardian_address}
          onChange={handleChange}
          placeholder="Enter guardian's address"
          rows={2}
        />
      </div>

      {renderSectionHeading(
        <MdContactPhone />,
        "Emergency Contact",
        "Person to reach when the father or guardian is unavailable.",
      )}

      <div className="enrollment-field">
        <label htmlFor="emergency-contact-name">Emergency Contact Name *</label>
        <input
          id="emergency-contact-name"
          name="emergency_contact_name"
          type="text"
          value={formData.emergency_contact_name}
          onChange={handleChange}
          placeholder="Enter emergency contact name"
          className={errors.emergency_contact_name ? "has-error" : ""}
        />
        {renderFieldError("emergency_contact_name")}
      </div>

      <div className="enrollment-field">
        <label htmlFor="emergency-contact-number">
          Emergency Contact Number
        </label>
        <input
          id="emergency-contact-number"
          name="emergency_contact_number"
          type="tel"
          value={formData.emergency_contact_number}
          onChange={handleChange}
          placeholder="e.g. 03XX-XXXXXXX (optional)"
        />
      </div>

    </>
  );

  const renderPreviousAcademicFields = () => {
    if (!needsPreviousAcademicDetails) return null;

    return (
      <>
        {renderSectionHeading(
          <MdHistoryEdu />,
          "Previous Academic Details",
          `Required because ${formData.class_name || "the selected class"} is above the lowest class offered in this session.`,
        )}

        <div className="enrollment-field">
          <label htmlFor="previous-school-name">Previous School Name *</label>
          <input
            id="previous-school-name"
            name="previous_school_name"
            type="text"
            value={formData.previous_school_name}
            onChange={handleChange}
            placeholder="Enter previous school name"
            className={errors.previous_school_name ? "has-error" : ""}
          />
          {renderFieldError("previous_school_name")}
        </div>

        <div className="enrollment-field">
          <label htmlFor="previous-class">Previous Class *</label>
          <input
            id="previous-class"
            name="previous_class"
            type="text"
            value={formData.previous_class}
            onChange={handleChange}
            placeholder="e.g. Class 4"
            className={errors.previous_class ? "has-error" : ""}
          />
          {renderFieldError("previous_class")}
        </div>

        <div className="enrollment-field">
          <label htmlFor="previous-grade">Last Class Grade / Percentage *</label>
          <input
            id="previous-grade"
            name="previous_grade"
            type="text"
            value={formData.previous_grade}
            onChange={handleChange}
            placeholder="e.g. A, 82%, Passed"
            className={errors.previous_grade ? "has-error" : ""}
          />
          {renderFieldError("previous_grade")}
        </div>

        <div className="enrollment-field span-2">
          <label htmlFor="previous-academic-record">Previous Academic Remarks *</label>
          <textarea
            id="previous-academic-record"
            name="previous_academic_record"
            value={formData.previous_academic_record}
            onChange={handleChange}
            placeholder="Enter academic performance, strengths, concerns, or other remarks"
            className={errors.previous_academic_record ? "has-error" : ""}
            rows={4}
          />
          {renderFieldError("previous_academic_record")}
        </div>
      </>
    );
  };

  const renderAcademicFields = () => (
    <>
      {userRole === "superadmin" && (
        <div className="enrollment-field">
          <label htmlFor="student-campus">Campus *</label>
          <select
            id="student-campus"
            name="campus_id"
            value={formData.campus_id}
            onChange={handleChange}
            className={errors.campus_id ? "has-error" : ""}
          >
            <option value="">Select campus</option>
            {campuses.map((campus) => (
              <option key={campus.id} value={campus.id}>
                {campus.name}
              </option>
            ))}
          </select>
          {renderFieldError("campus_id")}
        </div>
      )}

      <div className="enrollment-field">
        <label htmlFor="student-session">Academic Session *</label>
        <select
          id="student-session"
          name="academic_session"
          value={formData.academic_session}
          onChange={handleChange}
          disabled={
            isLoadingOptions ||
            (userRole === "superadmin" && !formData.campus_id)
          }
          className={errors.academic_session ? "has-error" : ""}
        >
          <option value="">
            {userRole === "superadmin" && !formData.campus_id
              ? "Select campus first"
              : isLoadingOptions
                ? "Loading sessions..."
                : "Select academic session"}
          </option>
          {sessions.map((session) => (
            <option key={session.id} value={session.name}>
              {session.name}
            </option>
          ))}
        </select>
        {renderFieldError("academic_session")}
      </div>

      <div className="enrollment-field">
        <label htmlFor="student-class">Class to Enroll In *</label>
        <select
          id="student-class"
          name="class_id"
          value={formData.class_id}
          onChange={handleChange}
          disabled={
            isLoadingOptions ||
            !formData.academic_session ||
            (userRole === "superadmin" && !formData.campus_id)
          }
          className={errors.class_id ? "has-error" : ""}
        >
          <option value="">
            {userRole === "superadmin" && !formData.campus_id
              ? "Select campus first"
              : isLoadingOptions
                ? "Loading classes..."
                : formData.academic_session
                  ? "Select class"
                  : "Select session first"}
          </option>
          {sessionClasses.map((classItem) => (
            <option key={classItem.id} value={classItem.id}>
              {classItem.name}
            </option>
          ))}
        </select>
        {renderFieldError("class_id")}
      </div>

      <div className="enrollment-field">
        <label htmlFor="student-section">Section *</label>
        {enrollmentSectionOptions.length ? (
          <select
            id="student-section"
            name="class_section"
            value={formData.class_section}
            onChange={handleChange}
            disabled={!formData.class_id}
            className={errors.class_section ? "has-error" : ""}
          >
            <option value="">
              {formData.class_id ? "Select section" : "Select class first"}
            </option>
            {enrollmentSectionOptions.map((section) => (
              <option key={section} value={section}>
                Section {section}
              </option>
            ))}
          </select>
        ) : (
          <input
            id="student-section"
            name="class_section"
            type="text"
            value={formData.class_section}
            onChange={handleChange}
            placeholder="e.g. A, B, C"
            className={errors.class_section ? "has-error" : ""}
          />
        )}
        {renderFieldError("class_section")}
      </div>
    </>
  );

  if (!isEdit && mode === "choose") {
    return (
      <div className="enrollment-choice-page">
        <div className="enrollment-choice-grid">
          <button
            type="button"
            className="enrollment-choice-card"
            onClick={() => enterMode("single")}
          >
            <div className="enrollment-choice-icon">
              <MdPersonOutline />
            </div>
            <h3>Add Student</h3>
            <p>
              Register one student at a time with full personal and academic
              details.
            </p>
            <span className="enrollment-choice-link">Get Started &rarr;</span>
          </button>

          {(userRole === "superadmin" ||
            userRole === "principal" ||
            userRole === "admin" ||
            userRole === "administrator") && (
            <button
              type="button"
              className="enrollment-choice-card accent"
              onClick={() => enterMode("bulk")}
            >
              <div className="enrollment-choice-icon">
                <MdUploadFile />
              </div>
              <h3>Add Students in Bulk</h3>
              <p>Upload a CSV file to register many students at once.</p>
              <span className="enrollment-choice-link">Upload CSV &rarr;</span>
            </button>
          )}
        </div>
      </div>
    );
  }

  if (
    !isEdit &&
    mode === "bulk" &&
    (userRole === "superadmin" ||
      userRole === "principal" ||
      userRole === "admin" ||
      userRole === "administrator")
  ) {
    const bulkFieldsComplete = Boolean(
      (userRole !== "superadmin" || formData.campus_id) &&
      formData.academic_session &&
      formData.class_id &&
      formData.class_section,
    );
    return (
      <div className="enrollment-choice-page">
        <div className="enrollment-form-grid">
          {renderAcademicFields()}

          <div className="enrollment-divider span-2">
            <MdUploadFile /> Upload CSV File
          </div>

          <div className="enrollment-field span-2">
            <div className="enrollment-bulk-dropzone compact">
              <div className="enrollment-bulk-dropzone-icon">
                <MdUploadFile />
              </div>
              <h4>Upload your CSV file</h4>
              <p>
                The campus, session, class and section selected above are used
                as defaults; template rows can supply their own academic values.
              </p>
              {!bulkFieldsComplete && (
                <p className="bulk-upload-status error">
                  {userRole === "superadmin"
                    ? "Please fill in campus, session, class and section above before uploading."
                    : "Please fill in session, class and section above before uploading."}
                </p>
              )}
              <input
                ref={bulkFileInputRef}
                type="file"
                accept=".csv"
                onChange={handleCSVUpload}
              />
              <div
                className="enrollment-actions"
                style={{
                  justifyContent: "center",
                  marginTop: 4,
                  borderTop: "none",
                  background: "none",
                  padding: 0,
                }}
              >
                <button
                  type="button"
                  className="enrollment-secondary-btn"
                  onClick={downloadBulkTemplate}
                >
                  <MdDownload /> Download Template
                </button>
                <button
                  type="button"
                  className="enrollment-primary-btn"
                  onClick={() => bulkFileInputRef.current?.click()}
                  disabled={
                    bulkUploadState.status === "uploading" ||
                    !bulkFieldsComplete
                  }
                >
                  <MdUploadFile />
                  {bulkUploadState.status === "uploading"
                    ? "Uploading..."
                    : "Choose CSV File"}
                </button>
              </div>

              {bulkUploadState.message && (
                <p className={`bulk-upload-status ${bulkUploadState.status}`}>
                  {bulkUploadState.message}
                </p>
              )}

              {bulkUploadErrors.length > 0 && (
                <button
                  type="button"
                  className="enrollment-secondary-btn"
                  onClick={downloadBulkErrorReport}
                >
                  <MdDownload /> Download Error Report
                </button>
              )}
            </div>
          </div>
        </div>

        {showRegistrationSuccess && (
          <div className="enrollment-success-overlay">
            <div className="enrollment-success-card" style={{ maxWidth: 440 }}>
              <div className="enrollment-success-icon">
                <MdCheck />
              </div>
              <h3>{registrationSuccess.title}</h3>
              <p>{registrationSuccess.message}</p>
              {parentCredentials ? (
                <div
                  style={{
                    marginTop: 14,
                    textAlign: "left",
                    width: "100%",
                    padding: "12px 14px",
                    borderRadius: 12,
                    border: "1px solid #dbe2ef",
                    background: "#f7f8fc",
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 8 }}>
                    PARENT LOGIN CREDENTIALS
                  </div>
                  <div style={{ marginBottom: 6 }}>
                    <strong>Username:</strong> {parentCredentials.username}
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Password:</strong>{" "}
                    {parentCredentials.password || "(existing account — use current password)"}
                  </div>
                  <p style={{ margin: 0, fontSize: 13, color: "#64748b" }}>
                    {parentCredentials.message}
                  </p>
                  <button
                    type="button"
                    className="enrollment-btn enrollment-btn-primary"
                    style={{ marginTop: 12, width: "100%" }}
                    onClick={() => {
                      setShowRegistrationSuccess(false);
                      setParentCredentials(null);
                      onCancel();
                    }}
                  >
                    Done
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="enrollment-shell">
      <main className="enrollment-main">
        {(isEdit || mode === "single") && (
          <form className="enrollment-card" onSubmit={handleSubmit}>
            <div className="enrollment-content">
              <div className="enrollment-title-row">
                <div className="enrollment-title-main">
                  <div className="enrollment-title-icon">
                    <MdPersonOutline />
                  </div>
                  <div>
                    <h2>Student Details</h2>
                  </div>
                </div>
              </div>

              <div className="enrollment-form-grid">
                {renderPersonalFields()}

                {renderSectionHeading(
                  <MdSchool />,
                  "Academic & Class Information",
                  "Campus, session, class and section the student is enrolled into.",
                )}

                {renderAcademicFields()}
                {renderPreviousAcademicFields()}
              </div>
            </div>

            <div className="enrollment-actions">
              {isEdit && (
                <button
                  type="button"
                  className="enrollment-secondary-btn"
                  onClick={onCancel}
                  disabled={isSaving}
                >
                  <MdArrowBack />
                  Cancel
                </button>
              )}

              <button
                type="submit"
                className="enrollment-primary-btn"
                disabled={isSaving}
              >
                {isSaving
                  ? "Saving..."
                  : isEdit
                    ? "Update Record"
                    : "Register Student"}
                <MdCheck />
              </button>
            </div>
          </form>
        )}
      </main>

      {showRegistrationSuccess && (
        <div className="enrollment-success-overlay">
          <div className="enrollment-success-card" style={{ maxWidth: 440 }}>
            <div className="enrollment-success-icon">
              <MdCheck />
            </div>
            <h3>{registrationSuccess.title}</h3>
            <p>{registrationSuccess.message}</p>
            {parentCredentials ? (
              <div
                style={{
                  marginTop: 14,
                  textAlign: "left",
                  width: "100%",
                  padding: "12px 14px",
                  borderRadius: 12,
                  border: "1px solid #dbe2ef",
                  background: "#f7f8fc",
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 8 }}>
                  PARENT LOGIN CREDENTIALS
                </div>
                <div style={{ marginBottom: 6 }}>
                  <strong>Username:</strong> {parentCredentials.username}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <strong>Password:</strong>{" "}
                  {parentCredentials.password || "(existing account — use current password)"}
                </div>
                <p style={{ margin: 0, fontSize: 13, color: "#64748b" }}>
                  {parentCredentials.message}
                </p>
                <button
                  type="button"
                  className="enrollment-btn enrollment-btn-primary"
                  style={{ marginTop: 12, width: "100%" }}
                  onClick={() => {
                    setShowRegistrationSuccess(false);
                    setParentCredentials(null);
                    onCancel();
                  }}
                >
                  Done
                </button>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentForm;
